/**
 * The tenant's STOCK — the read, and the rule that selects the low-stock lines.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS ITS OWN FILE
 * ---------------------------------------------------------------------------
 * The stock read has a piece of POLICY attached to it: the low-stock threshold. The rule and
 * the read belong together, because the rule is what turns a list of balances into a list of
 * lines a merchant has to act on. Keeping them apart is how a threshold ends up applied in one
 * place and forgotten in another.
 *
 * ---------------------------------------------------------------------------
 * THE THRESHOLD IS A CONSOLE POLICY, AND IT IS CARRIED OUT
 * ---------------------------------------------------------------------------
 * The platform has no reorder-point concept: nothing in the API says what "too little" is. So
 * the threshold is this console's own rule, and the summary CARRIES it — the widget prints it,
 * so a reader can see which rule produced the number and ask for it to change.
 */
import { http } from '@/api/client/http';
import { StockBalanceListResponseSchema } from '@/api/schemas/inventory';
import { offerSkus } from '@/modules/dashboard/services/offers';
import {
  LOW_STOCK_THRESHOLD_UNITS,
  parseQuantity,
  type LowStockLine,
  type StockOptions,
  type StockSummary,
} from '@/modules/dashboard/services/summaries';

/**
 * The tenant's stock, counted.
 *
 * `includeSkus` asks for the low-stock lines to be NAMED. A line is keyed by the offer id and
 * the SKU lives on the offer, so naming it needs the offer read — which the actor may not be
 * entitled to make. A line that cannot be named carries `sku: null` rather than a made-up label.
 */
export async function readStock(options: StockOptions = {}): Promise<StockSummary> {
  const [response, skus] = await Promise.all([
    http.get('/inventory/stock-balances', StockBalanceListResponseSchema),
    options.includeSkus === true ? offerSkus() : Promise.resolve(new Map<string, string>()),
  ]);

  let unitsOnHand = 0;
  const lowStock: LowStockLine[] = [];

  for (const balance of response.data) {
    unitsOnHand += parseQuantity(balance.onHand);
    const available = parseQuantity(balance.available);
    if (available <= LOW_STOCK_THRESHOLD_UNITS) {
      lowStock.push({
        itemId: balance.itemId,
        sku: skus.get(balance.itemId) ?? null,
        locationId: balance.locationId,
        available,
        unitOfMeasure: balance.unitOfMeasure,
      });
    }
  }

  return {
    lines: response.data.length,
    unitsOnHand,
    lowStock,
    lowStockThresholdUnits: LOW_STOCK_THRESHOLD_UNITS,
  };
}
